'use client';

import classNames from 'classnames';
import {
  useId,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
} from 'react';

import { Button } from '../../..';

import './css/FeedbackBar.scss';

export interface FeedbackBarProps
  extends React.HtmlHTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  className?: string;
  hasClose?: boolean;
  hasFocus?: boolean;
  id?: string;
  isOpen?: boolean;
  renderHeading?: React.ReactNode;
  renderFooter?: React.ReactNode;
  renderIcon?: React.ReactNode;
  onClose?: () => void;
  /**
   * Sets the aria-label for the close button. Defaults to English for backward compatibility.
   */
  closeButtonAriaLabel?: string;
}

const closeIcon = (
  <svg
    focusable="false"
    aria-hidden="true"
    width="32"
    height="33"
    viewBox="0 0 32 33"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 0.444336C7.16344 0.444336 0 7.57462 0 16.3703C0 25.1659 7.16344 32.2962 16 32.2962C24.8366 32.2962 32 25.1659 32 16.3703C32 12.1464 30.3143 8.09562 27.3137 5.10893C24.3131 2.12224 20.2435 0.444336 16 0.444336ZM16 30.969C7.89982 30.969 1.33333 24.4329 1.33333 16.3703C1.33333 8.30759 7.89982 1.7715 16 1.7715C24.1002 1.7715 30.6667 8.30759 30.6667 16.3703C30.6667 20.2421 29.1214 23.9553 26.3709 26.6931C23.6204 29.4309 19.8898 30.969 16 30.969ZM16 15.428L21.16 10.3051L22.0933 11.2342L16.9467 16.3703L22.0933 21.5064L21.16 22.4354L16 17.3125L10.84 22.4354L9.90667 21.5064L15.0533 16.3703L9.90667 11.2342L10.84 10.3051L16 15.428Z"
      fill="#262626"
    />
  </svg>
);

export const FeedbackBar = forwardRef(
  (props: FeedbackBarProps, outerRef: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      hasClose,
      hasFocus,
      id,
      isOpen = true,
      renderFooter,
      renderHeading,
      renderIcon,
      onClose,
      closeButtonAriaLabel = 'close',
      ...spreadProps
    } = props;
    const customFeedbackBarId = useId();
    const feedbackBarId = id || customFeedbackBarId;
    const feedbackBarHeadingId = `${feedbackBarId}-heading`;
    const feedbackBarIconId = `${feedbackBarId}-icon`;
    const feedbackBarBodyId = `${feedbackBarId}-body`;
    const [isClosed, setIsClosed] = useState(!isOpen);
    const innerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(outerRef, () => innerRef.current!, []);

    useEffect(() => {
      if (hasFocus && innerRef.current) {
        innerRef.current.focus();
      }
      // TODO: see if adding hasFocus to the dependency array is safe
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
      setIsClosed(!isOpen);
    }, [isOpen]);

    const heading = (
      <div
        className="ps-feedback-bar-heading"
        id={feedbackBarHeadingId}
      >
        {renderHeading}
      </div>
    );

    const footer = <div className="ps-feedback-bar-footer">{renderFooter}</div>;

    const handleClose = () => {
      setIsClosed(!isClosed);
      if (onClose) {
        onClose();
      }
    };

    const feedbackBar = classNames({
      'ps-feedback-bar': true,
      'ps-feedback-bar-is-closed': isClosed,
      [`${className}`]: className,
    });

    return (
      <div
        {...spreadProps}
        className={feedbackBar}
        id={feedbackBarId}
        ref={innerRef}
        role="region"
        {...(renderHeading ? { 'aria-labelledby': feedbackBarHeadingId } : '')}
        aria-labelledby={
          renderHeading
            ? `${feedbackBarIconId} ${feedbackBarHeadingId}`
            : `${feedbackBarIconId} ${feedbackBarBodyId}`
        }
        tabIndex={hasFocus ? -1 : undefined}
      >
        <div
          className="ps-feedback-bar-icon"
          id={feedbackBarIconId}
        >
          {renderIcon}
        </div>
        <div className="ps-feedback-bar-content">
          {renderHeading && heading}
          <div
            className="ps-feedback-bar-body"
            id={feedbackBarBodyId}
          >
            {children}
          </div>
          {renderFooter && footer}
        </div>
        {hasClose && (
          <div className="ps-feedback-bar-close">
            <Button
              aria-controls={feedbackBarId}
              aria-label={closeButtonAriaLabel}
              onClick={handleClose}
              renderIcon={closeIcon}
              size="sm"
              variant="text"
            ></Button>
          </div>
        )}
      </div>
    );
  }
);
